import { 
  Badge, 
  Box, 
  Flex, 
  FormControl, 
  FormErrorMessage, 
  FormLabel, 
  IconButton, 
  Input, 
  InputGroup, 
  InputLeftElement, 
  InputRightElement, 
  Radio, 
  RadioGroup, 
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MdClose, MdNavigateBefore, MdNavigateNext, MdOutlineFirstPage, MdOutlineLastPage } from 'react-icons/md';

import { MasterOutlet } from '@app/api/models';
import { AutocompleteFieldProps, Txt } from '@app/components/common';
import { useApi } from '@app/hooks';
import { useSelectKeyboard } from '@app/components/common/hooks/useSelectKeyboard';
import { Dropdown } from '@app/components/common/dropdown';
import { eventValue, getPropValue } from '@app/utils';

const attributesOptions = [
  { id: 'all', name: 'Везде' },
  { id: 'id', name: 'ID' },
  { id: 'name', name: 'Название' },
  { id: 'legal_name', name: 'Юр. наимен.' }, 
  // { id: 'title', name: 'Вывеска' }, 
  { id: 'inn', name: 'ИНН' },  
];

type SearchParams = { search: string, attribute: string, limit: number, offset: number };

export const OutletField = (props: AutocompleteFieldProps & { status?: 'all' | 'opened', distributor_id?: string }) => {
  const api = useApi();

  const onSearch = async ({ search, attribute, limit, offset }: SearchParams) => {
    const response = await api.acb.searchOutlet({
      search, 
      attribute,
      distributor_id: props.distributor_id,
      limit,
      offset,
      status: props.status === 'all' ? 'все' : 'открыта' // TODO: change to 'opened' 
    });

    return response.data;
  };

  return <>
    <AutocompleteOutletField
      {...props}
      onFetchOptions={onSearch}
      name='outlet'
      valueProperty={['legal_name']}
      renderOption={
        (option: MasterOutlet) => <Flex direction='column' gap={1}>
          <Flex justify='space-between' alignItems='flex-start'>
            <Flex gap={1}>
              <Txt color='gray.500' >[{option.id}]</Txt>
              <Txt >{option.legal_name}</Txt>
            </Flex>
            <Badge colorScheme='brand' m={1}>{option.status.name}</Badge>
          </Flex>
          <Txt color='gray.400' >{option.full_address}</Txt>
        </Flex>
      }
    />
  </>;
};

export const AutocompleteOutletField  = (props: Omit<AutocompleteFieldProps, 'onFetchOptions'> & {
  onFetchOptions?: (params: SearchParams) => Promise<Paginated<'data', MasterOutlet>> 
  minLengthToSearch?: number
}) => {
  const {
    id,
    label,
    value,
    error,
    helperText,
    valueProperty = 'name',
    renderOption,
    required,
    disabled,
    multiple,
    onChange,
    onFetchOptions,
    minLengthToSearch = 4,
    readOnly = false,
  } = props;
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchField, setSearchField] = useState<EntityBase>(attributesOptions[0]);

  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [options, setOptions] = useState<EntityBase[]>(
    props.options || []
  );
  const [inputValue, setInputValue] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const fetchOptionsTimerRef = useRef<any>();

  useEffect(() => {
    setInputValue(getPropValue(value as EntityBase, valueProperty) || value?.toString() || '');
  }, [value, valueProperty]);

  const onChangeInput = ({text, page = 1, attribute = 'all'}: {
    text: string, 
    page?: number, 
    attribute?: string
  }) => {
    setInputValue(text);
    if (attribute !== 'id' && text.length < minLengthToSearch) {
      return;
    }
    if (!onFetchOptions) {
      return;
    }
    setLoading(true);
    setPage(page);
    clearTimeout(fetchOptionsTimerRef.current);
    fetchOptionsTimerRef.current = setTimeout(() => {
      onFetchOptions({ 
        search: text, 
        attribute, 
        limit: 10, 
        offset: 10 * (page - 1),
      })
        .then(({ data, total }) => {
          setOptions(data || []);
          setTotal(total);
          if (data.length === 1 && getPropValue(data[0], valueProperty) === text) {
            onChange(data[0]);
          }
        })
        .finally(() => setLoading(false));
    }, 800);
  };

  const handleClickOutside = () => {
    document.removeEventListener('mousedown', handleClickOutside);
    setOpen(false);
  };

  const handleMouseLeave = () => {
    document.addEventListener('mousedown', handleClickOutside);
  };

  const handleMouseEnter = () => {
    document.removeEventListener('mousedown', handleClickOutside);
  };


  const handleOptionSelect = (option: EntityBase) => () => {
    setOpen(false);
    onChange?.(option);
    setPage(1);
    setTotal(0);
    setOptions([]);
    setInputValue(getPropValue(option as EntityBase, valueProperty) || option?.toString() || '');
  };

  const handleClear = () => {
    setInputValue('');
    setPage(1);
    setTotal(0);
    setOptions([]);
    onChange?.(undefined);
  };

  const onSearchFieldChange = (itemId: string) => {
    setSearchField(attributesOptions.find(({ id }) => id === itemId));
    onChangeInput({ text: inputValue, attribute: itemId });
  };

  const onPageChange = (page: number) => {
    onChangeInput({ text: inputValue, page, attribute: searchField.id.toString() });
  };


  const { marker, onKeyDown } = useSelectKeyboard({
    options,
    onChange,
    setOpen,
  });

  return (<InputGroup>
    <FormControl 
      variant="floating" 
      id={id} 
      isRequired={required}
      isInvalid={error}
      isDisabled={disabled}
      ref={fieldRef}
      
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <InputRightElement pr={0}>
        {!multiple && !disabled && <IconButton icon={<MdClose />} aria-label="clear" 
          backgroundColor="transparent" 
          borderRadius="50%"
          size="xs"
          onClick={handleClear}
        />}
      </InputRightElement>
      <Dropdown 
        fieldRef={fieldRef}
        open={open}
        setOpen={setOpen}
        overflowY='clip'
      >
        <Flex gap={2} alignItems='flex-start' px={4} py={1} justify='flex-start' >
          <Txt>Искать:</Txt>
          <RadioGroup value={searchField.id.toString()} onChange={onSearchFieldChange}>
            <Flex columnGap={3} rowGap={1} pt={0.5} flexWrap='wrap'>
            {attributesOptions.map((item) => {
              return <Radio size='sm' key={item.id} value={item.id}>{item.name}</Radio>;
            })}
            </Flex>
          </RadioGroup>
        </Flex>
        <Box overflow='auto' maxHeight={400}>
          {!loading && options.length === 0 
            && inputValue.length === 0 
            && <Dropdown.Label label="Начните ввод" />}
          {!loading && options.length === 0 
            && inputValue.length !== 0 
            && <Dropdown.Label label="Ничего не найдено"/>}
          {loading && <Dropdown.Loader />}
          {options.map((item, index) => {
            if (renderOption) {
              return <Dropdown.Option 
                key={item.id} 
                hovered={marker === index}
                onClick={handleOptionSelect(item)}
              >{renderOption(item)}</Dropdown.Option>;
            }
            return <Dropdown.Option
              key={item.id}
              hovered={marker === index}
              onClick={handleOptionSelect(item)}
            >
                {getPropValue(item, valueProperty)} 
              </Dropdown.Option>; 
          })}
        </Box>
        {total > 10 && <Box>
          <PaginationComponent 
            page={page} 
            pageSize={10} 
            total={total} 
            onPageChange={onPageChange} 
          />
        </Box>}
      </Dropdown>

      {value && <InputLeftElement pointerEvents='none' w={100}>
        <Txt color='gray.500'>[{(value as EntityBase)?.id}]</Txt>
      </InputLeftElement>}
      {!multiple && <Input 
        placeholder={label}
        paddingLeft={value ? 100 : 4}
        value={inputValue}
        contentEditable={false}
        onClick={() => !readOnly && setOpen(true)}
        onChange={eventValue((value) => !readOnly && onChangeInput({
          text: value,
          page: 1,
          attribute: searchField.id.toString(),
         }))}
        onKeyDown={onKeyDown}
        paddingEnd={8}
      />}
      <FormLabel textColor="dark.300">{label}</FormLabel>
      {error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>
  </InputGroup>);

};

type PaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange(page: number): void;
}

const PaginationComponent = ({ page, pageSize, total, onPageChange }: PaginationProps) => {
  
  const lastPage = useMemo(
    () => Math.ceil(total / pageSize),
    [total, pageSize]
  );

  const pages: number[] = useMemo(() => {
    const pagesRangeGenerator = function* () {
      let i = 1;
      while (i <= lastPage) {
        // check a page is at the beginning or at the end or around the current page
        if (i >= page - 2 && i <= page + 2) {
          yield i;
        }
        i++;
      }
    };
    return [...pagesRangeGenerator()];
  }, [page, lastPage]);

  const onPrevPageClick = () => {
    page !== 1 && onPageChange(page - 1);
  };

  const onNextPageClick = () => {
    page !== lastPage && onPageChange(page + 1);
  };
  
  return <Box px={6} py={4} >
    <Box display='flex' gap={8} alignItems='center' flexWrap="wrap"> 
      <Box>
        <Txt>{`${(page - 1)*pageSize + 1}-${Math.min(total, page * pageSize)} из ${total}`}</Txt>
      </Box>
      <Box display='flex' gap={2} alignItems='center'>
        <IconButton size='sm' icon={<MdOutlineFirstPage />} aria-label="first-page" onClick={() => onPageChange(1)}     />
        <IconButton size='sm' 
          icon={<MdNavigateBefore />} aria-label="prev-page" 
          opacity={page === 1 ? 0.5 : 1} onClick={onPrevPageClick} 
        />
        {pages.map((_page, index) => (
            <IconButton
              size='sm'
              key={`${_page}-${index}`}
              _active={{
                backgroundColor: 'brand.500',
                color: 'white',
              }}
              isActive={_page === page}
              onClick={() => onPageChange(_page)}
              icon={<Txt fontSize='sm'>{_page}</Txt>}
              aria-label={`page-${_page}`}
              
            />
          ))}
        <IconButton size='sm' 
          icon={<MdNavigateNext />} aria-label="next-page"
          opacity={page === lastPage ? 0.5 : 1} onClick={onNextPageClick} 
        />
        <IconButton size='sm' icon={<MdOutlineLastPage />} aria-label="last-page" onClick={() => onPageChange(lastPage)}/>
      </Box>
    </Box>
  </Box>;
};
